import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config';
import { FileTextIcon, TargetIcon, ReloadIcon, CheckIcon, CheckCircledIcon, Cross2Icon, ClockIcon, LightningBoltIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { useContentVertex } from '../contexts/ContentVertexContext';
import { getCountryCodeFromLocationCode } from '../data/locations';

const DEFAULTS_KEY = 'cv_quick_post_defaults';
const loadDefaults = () => {
  try { return JSON.parse(localStorage.getItem(DEFAULTS_KEY) || '{}'); }
  catch { return {}; }
};

const GlassSelect = ({ label, value, onChange, options, placeholder, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative" ref={selectRef}>
      {label && <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">{label}</label>}
      <div
        className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-zinc-400" />}
          <span className={selectedOption ? 'text-white' : 'text-zinc-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDownIcon className={`h-4 w-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 w-full z-50 bg-zinc-950 border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-zinc-600">
          {options.map((option, index) => option.disabled ? (
            <div
              key={`group-${index}`}
              className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-widest text-zinc-500 select-none"
            >
              {option.label.replace(/─/g, '').trim()}
            </div>
          ) : (
            <div
              key={`${option.value}-${index}`}
              className={`px-3 py-2.5 text-sm cursor-pointer transition-colors ${value === option.value
                ? 'bg-violet-500/20 text-violet-200'
                : 'text-zinc-300 hover:bg-violet-500/20 hover:text-violet-200'
                }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function QuickPost({ user, onClose, prefilledTopic = '', prefilledLocation = '', prefilledMonth = '', useMonthScheduler = false, hideKeywordSuggestion = false, hideClusterTab = false, hideKeywordField = false }) {
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('single');

  const getPrePublishMonth = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const idx = months.indexOf(month);
    if (idx === -1) return month;
    return months[(idx - 1 + 12) % 12];
  };

  const [scheduleMonth, setScheduleMonth] = useState(prefilledMonth ? getPrePublishMonth(prefilledMonth) : '');
  const [scheduleDay, setScheduleDay] = useState(prefilledMonth ? '15' : '');
  const [scheduleTime, setScheduleTime] = useState('10:00');

  const [formData, setFormData] = useState(() => {
    const d = loadDefaults();
    return {
      topic_title: prefilledTopic,
      target_location: prefilledLocation ? getCountryCodeFromLocationCode(prefilledLocation) : (d.target_location || 'US'),
      location_code: prefilledLocation ? parseInt(prefilledLocation) : (d.location_code || 2840),
      wordpress_site_id: d.wordpress_site_id || '',
      category_id: '',
      publish_type: d.publish_type || 'draft',
      schedule_type: prefilledMonth ? 'scheduled' : 'now',
      scheduled_at: '',
      article_type: 'standard',
      ai_model: d.ai_model || 'gemini-2.5-flash',
      article_length: d.article_length || 1600,
      include_images: d.include_images ?? false,
    };
  });
  const [requiresReview, setRequiresReview] = useState(() => loadDefaults().requires_review ?? false);
  const [showProgress, setShowProgress] = useState(false);
  const [topicProgress, setTopicProgress] = useState(0);
  const [progressSteps, setProgressSteps] = useState([
    { id: 1, label: 'Creating Topic...', status: 'pending', description: 'Setting up your article', icon: 'file' },
    { id: 2, label: 'Generating Outline...', status: 'pending', description: 'Planning article structure', icon: 'brain' },
    { id: 3, label: 'Writing Content...', status: 'pending', description: 'Creating full article', icon: 'edit' },
    { id: 4, label: 'Saving...', status: 'pending', description: 'Storing in WordPress', icon: 'save' },
    { id: 5, label: 'Complete!', status: 'pending', description: 'All done!', icon: 'check' }
  ]);
  const [error, setError] = useState(null);
  const [currentTopicId, setCurrentTopicId] = useState(null);

  const [extractedKeyword, setExtractedKeyword] = useState('');
  const [isExtractingKeyword, setIsExtractingKeyword] = useState(false);
  const [keywordEditable, setKeywordEditable] = useState(false);
  const [keywordExtractionFailed, setKeywordExtractionFailed] = useState(false);

  const [clusterFormData, setClusterFormData] = useState(() => {
    const d = loadDefaults();
    return {
      seed_keyword: prefilledTopic,
      wordpress_site_id: d.wordpress_site_id || '',
      category_id: '',
      publish_type: d.publish_type || 'draft',
      requires_review: d.requires_review ?? false,
      ai_model: d.ai_model || 'gemini-2.5-flash',
      article_length: d.article_length || 1200,
      include_images: d.include_images ?? false,
      include_faq: true,
      include_takeaways: true,
      schedule_type: 'now',
      start_date: '',
      interval_days: 0,
      target_location: prefilledLocation ? getCountryCodeFromLocationCode(prefilledLocation) : (d.target_location || 'US'),
      location_code: prefilledLocation ? parseInt(prefilledLocation) : (d.location_code || 2840),
    };
  });
  const [clusterSuccess, setClusterSuccess] = useState(null);
  const [clusterLoading, setClusterLoading] = useState(false);

  // Product Review (Affiliate) tab state
  const [affiliateFormData, setAffiliateFormData] = useState(() => {
    const d = loadDefaults();
    return {
      topic: '',
      wordpress_site_id: d.wordpress_site_id || '',
      category_id: '',
      publish_type: d.publish_type || 'draft',
      num_products: 5,
      ai_model: d.ai_model || 'gemini-2.5-flash',
      target_location: d.target_location || 'US',
      location_code: d.location_code || 2840,
    };
  });
  const [amazonTag, setAmazonTag] = useState('');
  const [amazonTagSaved, setAmazonTagSaved] = useState(false);
  const [savingAmazonTag, setSavingAmazonTag] = useState(false);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateSuccess, setAffiliateSuccess] = useState(false);
  const [affiliateError, setAffiliateError] = useState(null);

  const [batchText, setBatchText] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const { niche } = useContentVertex();

  const NICHE_BENEFIT = {
    affiliate:  'A revenue-ready product review with Amazon affiliate links — in under 3 minutes.',
    blog:       'A fully-researched SEO article — ready in 3 minutes, worth ~$150 in organic traffic.',
    news:       'Breaking news coverage that gets indexed fast — generated in under 3 minutes.',
    ecommerce:  'A product-optimised SEO article driving purchase intent — done in 3 minutes.',
    agency:     'A client-ready article with full SEO optimisation — delivered in 3 minutes.',
    cluster:    'A full 10-article content strategy — done in one click. Saves ~40 hours of planning.',
  };

  const [outcomePreview, setOutcomePreview] = useState(null);

  const buildOutcomePreview = useCallback((topic) => {
    if (!topic || topic.trim().length < 6) { setOutcomePreview(null); return; }
    const wordCount = formData.article_length || 1600;
    const minutesSaved = Math.round((wordCount / 500) * 60);
    const hoursSaved   = (wordCount / 500).toFixed(1);
    setOutcomePreview({ hoursSaved, minutesSaved });
  }, [formData.article_length]);

  useEffect(() => {
    const timer = setTimeout(() => buildOutcomePreview(formData.topic_title), 800);
    return () => clearTimeout(timer);
  }, [formData.topic_title, buildOutcomePreview]);

  // Persist sticky fields to localStorage whenever they change
  useEffect(() => {
    const saved = loadDefaults();
    const siteId = formData.wordpress_site_id;
    localStorage.setItem(DEFAULTS_KEY, JSON.stringify({
      ...saved,
      wordpress_site_id: formData.wordpress_site_id,
      ai_model: formData.ai_model,
      article_length: formData.article_length,
      publish_type: formData.publish_type,
      target_location: formData.target_location,
      location_code: formData.location_code,
      include_images: formData.include_images,
      requires_review: requiresReview,
      category_by_site: {
        ...(saved.category_by_site || {}),
        ...(siteId && formData.category_id ? { [String(siteId)]: formData.category_id } : {}),
      },
    }));
  }, [
    formData.wordpress_site_id, formData.category_id, formData.ai_model,
    formData.article_length, formData.publish_type, formData.target_location,
    formData.location_code, formData.include_images, requiresReview,
  ]);

  useEffect(() => {
    loadWordPressSites();
  }, []);

  useEffect(() => {
    if (useMonthScheduler && scheduleMonth && scheduleDay && scheduleTime) {
      const monthMap = {
        'January': 0, 'February': 1, 'March': 2, 'April': 3,
        'May': 4, 'June': 5, 'July': 6, 'August': 7,
        'September': 8, 'October': 9, 'November': 10, 'December': 11
      };

      const currentYear = new Date().getFullYear();
      const selectedMonth = monthMap[scheduleMonth];
      const currentMonth = new Date().getMonth();

      const year = selectedMonth < currentMonth ? currentYear + 1 : currentYear;

      const [hours, minutes] = scheduleTime.split(':');
      const scheduledDate = new Date(year, selectedMonth, parseInt(scheduleDay), parseInt(hours), parseInt(minutes));

      const formatted = scheduledDate.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, scheduled_at: formatted }));
    }
  }, [scheduleMonth, scheduleDay, scheduleTime, useMonthScheduler]);

  const loadWordPressSites = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      const loadedSites = data.sites || [];
      setSites(loadedSites);

      // Restore saved site's categories and category selection
      const savedDefaults = loadDefaults();
      const savedSiteId = savedDefaults.wordpress_site_id;
      if (savedSiteId && loadedSites.some(s => String(s.id) === String(savedSiteId))) {
        setLoadingCategories(prev => ({ ...prev, [savedSiteId]: true }));
        try {
          const catResponse = await fetch(`${config.apiUrl}/wordpress-sites/${savedSiteId}/categories`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            }
          });
          const catData = await catResponse.json();
          const loadedCats = catData.categories || [];
          setCategories(prev => ({ ...prev, [savedSiteId]: loadedCats }));

          const savedCategoryId = (savedDefaults.category_by_site || {})[String(savedSiteId)];
          if (savedCategoryId && loadedCats.some(c => String(c.id) === String(savedCategoryId))) {
            setFormData(prev => ({ ...prev, category_id: savedCategoryId }));
            setClusterFormData(prev => ({ ...prev, wordpress_site_id: savedSiteId, category_id: savedCategoryId }));
            setAffiliateFormData(prev => ({ ...prev, wordpress_site_id: savedSiteId, category_id: savedCategoryId }));
          }
        } finally {
          setLoadingCategories(prev => ({ ...prev, [savedSiteId]: false }));
        }
      }
    } catch {
    }
  };

  const applySiteDefaults = (updates, siteId) => {
    const site = sites.find(s => String(s.id) === String(siteId));
    const siteDef = site?.generation_defaults || {};
    const localDef = loadDefaults();
    return {
      ai_model:      siteDef.ai_model      || localDef.ai_model      || 'gemini-2.5-flash',
      article_length: siteDef.article_length || localDef.article_length || 1600,
      publish_type:  siteDef.publish_type  || localDef.publish_type  || 'draft',
      include_images: siteDef.include_images ?? localDef.include_images ?? false,
      ...updates,
    };
  };

  const handleSiteChange = async (siteId) => {
    setFormData(prev => ({ ...prev, ...applySiteDefaults({ wordpress_site_id: siteId, category_id: '' }, siteId) }));
    if (siteId) {
      await loadCategories(siteId);
    }
  };

  const handleClusterSiteChange = async (siteId) => {
    setClusterFormData(prev => ({ ...prev, ...applySiteDefaults({ wordpress_site_id: siteId, category_id: '' }, siteId) }));
    if (siteId) {
      await loadCategories(siteId);
    }
  };

  const handleAffiliateSiteChange = async (siteId) => {
    setAffiliateFormData(prev => ({ ...prev, ...applySiteDefaults({ wordpress_site_id: siteId, category_id: '' }, siteId) }));
    setAmazonTagSaved(false);
    if (siteId) {
      await loadCategories(siteId);
      const site = sites.find(s => String(s.id) === String(siteId));
      setAmazonTag(site?.amazon_tag || '');
    } else {
      setAmazonTag('');
    }
  };

  const saveAmazonTag = async () => {
    if (!affiliateFormData.wordpress_site_id) return;
    setSavingAmazonTag(true);
    try {
      await fetch(`${config.apiUrl}/wordpress-sites/${affiliateFormData.wordpress_site_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amazon_tag: amazonTag })
      });
      setSites(sites.map(s => String(s.id) === String(affiliateFormData.wordpress_site_id) ? { ...s, amazon_tag: amazonTag } : s));
      setAmazonTagSaved(true);
      setTimeout(() => setAmazonTagSaved(false), 2500);
    } catch {
    } finally {
      setSavingAmazonTag(false);
    }
  };

  // Auto-detect single product intent from topic title
  const isSingleProductTopic = (() => {
    const t = affiliateFormData.topic.toLowerCase();
    if (!t) return false;
    // "review" keyword, "vs", specific model numbers (V15, X100, etc.), or "my [product]"
    return /\breview\b/.test(t) || /\bvs\.?\b/.test(t) || /\b[a-z]+\s?[vx]?\d{1,4}[a-z]?\b/.test(t);
  })();

  const handleGenerateAffiliateArticle = async (e) => {
    if (e) e.preventDefault();
    if (!affiliateFormData.topic || !affiliateFormData.wordpress_site_id) return;
    setAffiliateLoading(true);
    setAffiliateError(null);
    try {
      const response = await fetch(`${config.apiUrl}/topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic_title: affiliateFormData.topic,
          wordpress_site_id: affiliateFormData.wordpress_site_id,
          category_id: affiliateFormData.category_id || null,
          publish_type: affiliateFormData.publish_type,
          article_type: 'product_review',
          target_location: affiliateFormData.target_location,
          location_code: affiliateFormData.location_code,
          generation_settings: {
            ai_model: affiliateFormData.ai_model,
            num_products: isSingleProductTopic ? 1 : affiliateFormData.num_products,
          }
        })
      });
      const data = await response.json();
      if (data.success) {
        setAffiliateSuccess(true);
        setAffiliateFormData({
          topic: '',
          wordpress_site_id: '',
          category_id: '',
          publish_type: 'draft',
          num_products: 5,
          ai_model: 'gemini-2.5-flash',
          target_location: 'US',
          location_code: 2840,
        });
      } else {
        setAffiliateError(data.message || 'Failed to generate review article');
      }
    } catch {
      setAffiliateError('Failed to generate review article. Please try again.');
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handleBatchSubmit = async () => {
    const topics = batchText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (!topics.length || !formData.wordpress_site_id || !formData.category_id) return;
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const response = await fetch(`${config.apiUrl}/topics/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topics,
          wordpress_site_id: formData.wordpress_site_id,
          category_id: formData.category_id || null,
          publish_type: formData.publish_type,
          ai_model: formData.ai_model,
          article_length: formData.article_length,
          include_images: formData.include_images,
          target_location: formData.target_location,
          location_code: formData.location_code,
          requires_review: requiresReview,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setBatchResult({ queued: data.queued });
        setBatchText('');
      } else {
        setBatchResult({ error: data.message || 'Batch import failed' });
      }
    } catch (err) {
      setBatchResult({ error: 'Failed to queue topics. Please try again.' });
    } finally {
      setBatchLoading(false);
    }
  };

  const loadCategories = async (siteId) => {
    setLoadingCategories({ ...loadingCategories, [siteId]: true });
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites/${siteId}/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setCategories({ ...categories, [siteId]: data.categories || [] });
    } catch {
    } finally {
      setLoadingCategories({ ...loadingCategories, [siteId]: false });
    }
  };

  // Stop words excluded from meaningful word count (only for Extract pill threshold)
  const STOP_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'of', 'in', 'on', 'to', 'with', 'by', 'at', 'from', 'is', 'are', 'was', 'be', 'as', 'it', 'its']);

  // Meaningful word count — excludes stop words
  const topicMeaningfulWordCount = (formData.topic_title || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 0 && !STOP_WORDS.has(w))
    .length;

  // Raw word count (still used for other purposes)
  const topicWordCount = topicMeaningfulWordCount;

  // When topic changes — always clear keyword field. User fills it via Extract pill or manually.
  useEffect(() => {
    setExtractedKeyword('');
  }, [formData.topic_title]);

  // Manual AI extraction — triggered only by the Extract pill click
  const handleExtractKeyword = async () => {
    const topic = (formData.topic_title || '').trim();
    if (!topic || isExtractingKeyword) return;

    setIsExtractingKeyword(true);

    try {
      const response = await fetch(`${config.apiUrl}/extract-keyword`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ topic })
      });

      const data = await response.json();

      if (data.success && data.keyword) {
        setExtractedKeyword(data.keyword);
      } else {
        // AI failed — silently use topic title as-is
        setExtractedKeyword(topic.toLowerCase());
      }
    } catch (error) {
      // Network error — silently use topic title
      setExtractedKeyword(topic.toLowerCase());
    } finally {
      setIsExtractingKeyword(false);
    }
  };

  const updateStepStatus = (stepId, status, stepError = null) => {
    setProgressSteps(prevSteps =>
      prevSteps.map(step =>
        step.id === stepId ? { ...step, status, error: stepError } : step
      )
    );
  };

  const updateProgressSteps = (publishType) => {
    const steps = [
      { id: 1, label: 'Creating Topic...', status: 'pending', description: 'Setting up your article', icon: 'file' },
      { id: 2, label: 'Generating Outline...', status: 'pending', description: 'Planning article structure', icon: 'brain' },
      { id: 3, label: 'Writing Content...', status: 'pending', description: 'Creating full article', icon: 'edit' },
      { id: 4, label: publishType === 'publish' ? 'Publishing...' : 'Saving as Draft...', status: 'pending', description: publishType === 'publish' ? 'Publishing to WordPress' : 'Storing in WordPress', icon: 'save' },
      { id: 5, label: 'Complete!', status: 'pending', description: 'All done!', icon: 'check' }
    ];
    setProgressSteps(steps);
  };

  const handlePost = async (e) => {
    e.preventDefault();

    if (!formData.wordpress_site_id) {
      setError('Please select a WordPress site before generating.');
      return;
    }

    if (!formData.category_id) {
      setError('Please select a category before generating.');
      return;
    }

    if (formData.schedule_type === 'scheduled' && !formData.scheduled_at) {
      setError('Please select a date and time for scheduled posts');
      return;
    }

    setLoading(true);
    setError(null);
    setShowProgress(true);
    updateProgressSteps(formData.publish_type);
    updateStepStatus(1, 'loading');

    try {
      const payload = {
        topic_title: formData.topic_title,
        primary_keyword: extractedKeyword || null,
        wordpress_site_id: formData.wordpress_site_id,
        publish_type: formData.publish_type,
        schedule_type: formData.schedule_type,
        requires_review: requiresReview,
        generation_settings: {
          article_type: formData.article_type,
          ai_model: formData.ai_model,
          article_length: formData.article_length,
          include_images: formData.include_images
        }
      };

      if (formData.category_id) {
        payload.category_id = formData.category_id;
      }

      if (formData.schedule_type === 'scheduled' && formData.scheduled_at) {
        payload.scheduled_at = formData.scheduled_at;
        payload.user_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      const response = await fetch(`${config.apiUrl}/topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error(`Server error (${response.status}). The backend returned HTML instead of JSON.`);
      }

      if (!data.success) {
        updateStepStatus(1, 'error', data.message || 'Failed to create topic');
        setError(data.message || 'Failed to create topic');
        setLoading(false);
        return;
      }

      const newTopic = data.topic;
      setCurrentTopicId(newTopic.id);
      updateStepStatus(1, 'complete');
      updateStepStatus(2, 'loading');

      const processResponse = await fetch(`${config.apiUrl}/topics/${newTopic.id}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!processResponse.ok) {
        updateStepStatus(2, 'error', 'Failed to start content generation');
        setError('Failed to start content generation');
        setLoading(false);
        return;
      }

      await pollTopicStatus(newTopic.id, formData.publish_type, formData.schedule_type);

    } catch (err) {
      setError('An unexpected error occurred: ' + err.message);
      setLoading(false);
    }
  };

  const pollTopicStatus = async (topicId, publishType, scheduleType) => {
    const maxAttempts = 60;
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/topics/${topicId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const responseText = await response.text();
          let data;
          try {
            data = JSON.parse(responseText);
          } catch (jsonError) {
            throw new Error('Server returned invalid response. Please check the backend logs.');
          }
          const topic = data.topic;

          // Update 0-100 progress bar
          if (topic.progress !== undefined && topic.progress !== null) {
            setTopicProgress(topic.progress);
          }

          if (topic.status === 'pending') {
            updateStepStatus(1, 'complete');
            updateStepStatus(2, 'loading');
          } else if (topic.status === 'processing') {
            updateStepStatus(1, 'complete');
            updateStepStatus(2, 'complete');
            updateStepStatus(3, 'loading');
          } else if (topic.status === 'generated') {
            updateStepStatus(1, 'complete');
            updateStepStatus(2, 'complete');
            updateStepStatus(3, 'complete');

            if (scheduleType === 'scheduled') {
              updateStepStatus(4, 'complete');
              updateStepStatus(5, 'complete');
              setLoading(false);
              setTimeout(() => {
                setShowProgress(false);
                if (onClose) onClose('topics');
              }, 2000);
              return;
            } else {
              updateStepStatus(4, 'loading');
            }
          } else if (topic.status === 'needs_review') {
            updateStepStatus(1, 'complete');
            updateStepStatus(2, 'complete');
            updateStepStatus(3, 'complete');
            updateStepStatus(4, 'complete');
            updateStepStatus(5, 'complete');
            setLoading(false);
            setTimeout(() => {
              setShowProgress(false);
              if (onClose) onClose('topics');
            }, 2000);
            return;
          } else if (topic.status === 'draft' || topic.status === 'published') {
            updateStepStatus(1, 'complete');
            updateStepStatus(2, 'complete');
            updateStepStatus(3, 'complete');
            updateStepStatus(4, 'complete');
            updateStepStatus(5, 'complete');
            setLoading(false);
            setTimeout(() => {
              setShowProgress(false);
              if (onClose) onClose('topics');
            }, 2000);
            return;
          } else if (topic.status === 'failed') {
            updateStepStatus(3, 'error', topic.error_message || 'Content generation failed');
            setError(topic.error_message || 'Content generation failed');
            setLoading(false);
            return;
          }

          if (attempts < maxAttempts &&
            !((scheduleType === 'scheduled' && topic.status === 'generated') ||
              (scheduleType === 'now' && (topic.status === 'draft' || topic.status === 'published')) ||
              topic.status === 'needs_review') &&
            topic.status !== 'failed') {
            attempts++;
            setTimeout(checkStatus, 10000);
          } else if (attempts >= maxAttempts) {
            updateStepStatus(3, 'error', 'Processing timeout - please check Topic Queue');
            setError('Processing is taking longer than expected. Check Topic Queue for status.');
            setLoading(false);
          }
        }
      } catch {
        updateStepStatus(1, 'error', 'Failed to check status');
        setError('Failed to check processing status');
        setLoading(false);
      }
    };

    setTimeout(checkStatus, 5000);
  };

  const handleGenerateCluster = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!clusterFormData.wordpress_site_id) {
      setError('Please select a WordPress site.');
      return;
    }
    if (!clusterFormData.category_id) {
      setError('Please select a category before generating.');
      return;
    }

    // Validate scheduling
    if (clusterFormData.schedule_type === 'scheduled' && !clusterFormData.start_date) {
      setError('Please select a start date for scheduling.');
      return;
    }

    setClusterLoading(true);

    const clusterPayload = {
      ...clusterFormData,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const response = await fetch(`${config.apiUrl}/topics/cluster`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clusterPayload)
      });

      const data = await response.json();

      if (data.success) {
        setClusterSuccess({
          count: data.data?.topics_count || 11,
          pillar: data.data?.pillar_title || clusterFormData.seed_keyword,
        });
        setClusterFormData({
          seed_keyword: '',
          wordpress_site_id: '',
          category_id: '',
          publish_type: 'draft',
          requires_review: false,
          ai_model: 'gemini-2.5-flash',
          article_length: 1200,
          include_images: false,
          include_faq: true,
          include_takeaways: true,
          schedule_type: 'now',
          start_date: '',
          interval_days: 0,
          target_location: 'US',
          location_code: 2840,
        });
      } else {
        setError(data.message || 'Failed to generate cluster');
      }
    } catch {
      setError('Failed to generate cluster. Please try again.');
    } finally {
      setClusterLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setShowProgress(false);
    if (currentTopicId) {
      setShowProgress(true);
      updateProgressSteps(formData.publish_type);
      updateStepStatus(1, 'loading');
      fetch(`${config.apiUrl}/topics/${currentTopicId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      }).then(() => pollTopicStatus(currentTopicId, formData.publish_type, formData.schedule_type));
    }
  };

  const getWordCountColor = (count) => {
    if (count > 5) return 'text-red-400';
    if (count >= 4) return 'text-amber-400';
    if (count >= 2) return 'text-emerald-400';
    return 'text-zinc-500';
  };

  const getWordCountMessage = (count) => {
    if (count > 5) return '⚠️ Keyword too long! Maximum 5 words allowed.';
    if (count >= 4) return '💡 Tip: 2-3 words is optimal for SEO. Current length is acceptable but could be shorter.';
    if (count >= 2) return '✅ Perfect! 2-3 words is ideal for SEO and title generation.';
    if (count === 1) return '💡 Tip: Add 1-2 more words for better context (e.g., "meditation for beginners").';
    return '';
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-2xl rounded-xl p-[1px]" style={{background:'linear-gradient(135deg,#8b5cf6,#3b82f6,#06b6d4,#8b5cf6)',backgroundSize:'300% 300%',animation:'gradientShift 4s ease infinite'}}>
        <style>{`@keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}`}</style>
        {/* INNER CONTENT */}
        <div
          className="bg-zinc-950 w-full rounded-xl flex flex-col h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {!showProgress ? (
            <>
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-none">
                <h2 className="scroll-m-20 text-lg font-semibold tracking-tight bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                  Add New Topic
                </h2>
                <button
                  type="button"
                  className="text-zinc-400 hover:text-white text-2xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
                  onClick={onClose}
                >
                  ×
                </button>
              </div>

              {/* SCROLLABLE BODY (MIDDLE) */}
              <div className="flex-1 overflow-y-auto min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-800 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-zinc-700" style={{ scrollbarColor: '#27272a transparent', scrollbarWidth: 'thin', willChange: 'transform', transform: 'translateZ(0)' }}>
                {!hideClusterTab && (
                  <div className="px-6 pt-4 pb-2">
                    <div className="bg-zinc-900/50 p-1 rounded-lg flex gap-1">
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'single'
                          ? 'bg-white text-zinc-900'
                          : ''
                          }`}
                        onClick={() => setActiveTab('single')}
                      >
                        {activeTab === 'single' ? (
                          <>
                            <FileTextIcon className="h-4 w-4" />
                            Single Topic
                          </>
                        ) : (
                          <>
                            <FileTextIcon className="h-4 w-4 text-violet-400" />
                            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">Single Topic</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'cluster'
                          ? 'bg-white text-zinc-900'
                          : ''
                          }`}
                        onClick={() => setActiveTab('cluster')}
                      >
                        {activeTab === 'cluster' ? (
                          <>
                            <TargetIcon className="h-4 w-4" />
                            Cluster Generator
                          </>
                        ) : (
                          <>
                            <TargetIcon className="h-4 w-4 text-violet-400" />
                            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">Cluster Generator</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'affiliate'
                          ? 'bg-white text-zinc-900'
                          : ''
                          }`}
                        onClick={() => setActiveTab('affiliate')}
                      >
                        {activeTab === 'affiliate' ? (
                          <>
                            <span>🛒</span>
                            Product Review
                          </>
                        ) : (
                          <>
                            <span>🛒</span>
                            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">Product Review</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'batch'
                          ? 'bg-white text-zinc-900'
                          : ''
                          }`}
                        onClick={() => setActiveTab('batch')}
                      >
                        {activeTab === 'batch' ? (
                          <>
                            <span>📋</span>
                            Batch Import
                          </>
                        ) : (
                          <>
                            <span>📋</span>
                            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">Batch Import</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="px-6 py-4">
                  {activeTab === 'single' ? (
                    <form onSubmit={handlePost} className="space-y-6">

                      {/* Niche benefit statement */}
                      {niche && (
                        <div className="px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-xs text-violet-300 leading-relaxed">
                          {NICHE_BENEFIT[niche] || NICHE_BENEFIT.blog}
                        </div>
                      )}

                      {/* Outcome preview card — appears 800ms after typing */}
                      {outcomePreview && (
                        <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3 space-y-1.5 text-xs text-zinc-400">
                          <p className="font-semibold text-zinc-300 mb-1">What you'll get:</p>
                          <div className="flex items-center gap-2">
                            <span className="text-amber-400">⏱</span>
                            This article takes ~{outcomePreview.hoursSaved}h to write manually. We'll do it in ~3 minutes.
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-400">📈</span>
                            A {formData.article_length}-word SEO-optimised article, ready to rank.
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-blue-400">💰</span>
                            If it ranks, estimated traffic value: ~$150/month in organic visits.
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Topic</label>
                        <input
                          type="text"
                          className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors"
                          placeholder="Enter a descriptive topic title here..."
                          value={formData.topic_title}
                          onChange={(e) => setFormData({ ...formData, topic_title: e.target.value })}
                          required
                        />

                        {!hideKeywordSuggestion && !hideKeywordField && formData.topic_title && topicWordCount >= 4 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-zinc-400 flex items-center gap-1">
                                Primary Keyword
                                <span className="relative group/tip cursor-help">
                                  <svg className="h-3 w-3 text-zinc-500 hover:text-zinc-300 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-xs text-zinc-300 leading-relaxed opacity-0 group-hover/tip:opacity-100 pointer-events-none transition-opacity duration-200 z-50 shadow-xl">
                                    <p className="font-medium text-white mb-1">What is a Primary Keyword?</p>
                                    <p>It's the main phrase people type in Google to find your article. The AI extracts it from your topic title — keeping your intent (e.g. "how to", "free", "for small business") while removing fluff like "Best", "Top 10", or years.</p>
                                    <p className="mt-1 text-violet-400">You can always edit it manually.</p>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800" />
                                  </div>
                                </span>
                              </label>
                              {extractedKeyword && (
                                <span className={`text-xs font-medium ${getWordCountColor(extractedKeyword.split(' ').filter(w => w.length > 0).length)}`}>
                                  {extractedKeyword.split(' ').filter(w => w.length > 0).length}/5 words
                                </span>
                              )}
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                className={`h-11 w-full bg-white/5 border rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none transition-colors ${extractedKeyword.split(' ').filter(w => w.length > 0).length > 5
                                  ? 'border-red-500/50 focus:border-red-500'
                                  : 'border-white/10 focus:border-violet-500/50'
                                  } ${topicWordCount >= 4 ? 'pr-24' : 'pr-10'}`}
                                value={extractedKeyword}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  if (value.includes(',')) value = value.replace(/,/g, ' ');
                                  const words = value.split(' ').filter(w => w.length > 0);
                                  if (words.length > 5) value = words.slice(0, 5).join(' ');
                                  setExtractedKeyword(value);
                                }}
                                placeholder={topicWordCount >= 4 ? "Click Extract for primary keyword suggestion or type yours" : ""}
                                onFocus={() => setKeywordEditable(true)}
                                onBlur={() => setKeywordEditable(false)}
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                                {isExtractingKeyword ? (
                                  <ReloadIcon className="h-4 w-4 text-violet-400 animate-spin" />
                                ) : topicWordCount >= 4 ? (
                                  <button
                                    type="button"
                                    onClick={handleExtractKeyword}
                                    className="px-2 py-0.5 text-xs font-medium bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 hover:text-violet-200 border border-violet-500/30 rounded-full transition-all cursor-pointer"
                                  >
                                    ✨ Extract
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            {extractedKeyword && (
                              <p className={`text-xs mt-1.5 ${extractedKeyword.split(' ').filter(w => w.length > 0).length > 5 ? 'text-red-400' : 'text-zinc-500'}`}>
                                {getWordCountMessage(extractedKeyword.split(' ').filter(w => w.length > 0).length)}
                              </p>
                            )}

                          </div>
                        )}

                      </div>

                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Target Audience</label>
                        <GlassSelect
                          value={formData.target_location}
                          onChange={(value) => {
                            const locationData = require('../data/locations').DATAFORSEO_LOCATIONS[value];
                            setFormData({
                              ...formData,
                              target_location: value,
                              location_code: locationData?.code || 2840
                            });
                          }}
                          options={require('../data/locations').getLocationOptions()}
                          placeholder="Select Target Location"
                        />
                        <p className="text-xs text-zinc-500 mt-1.5">🌍 Content will be optimized for search trends in this location</p>
                      </div>

                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">AI Model</label>
                        <GlassSelect
                          value={formData.ai_model}
                          onChange={(value) => setFormData({ ...formData, ai_model: value })}
                          options={[
                            { value: '', label: '── Gemini ──', disabled: true },
                            { value: 'gemini-2.5-flash-lite', label: '⚡ Gemini 2.5 Flash Lite' },
                            { value: 'gemini-2.5-flash', label: '🔥 Gemini 2.5 Flash' },
                            { value: 'gemini-2.5-pro', label: '🌟 Gemini 2.5 Pro' },
                            { value: '', label: '── ChatGPT ──', disabled: true },
                            { value: 'gpt-4o-mini', label: '💰 GPT-4o Mini' },
                            { value: 'gpt-5-nano-2025-08-07', label: '💰 GPT-5 Nano' },
                            { value: 'gpt-5-mini-2025-08-07', label: '💰 GPT-5 Mini' },
                            { value: 'gpt-4.1-mini', label: '⚡ GPT-4.1 Mini' },
                            { value: 'gpt-5.2', label: '🚀 GPT-5.2' },
                            { value: '', label: '── Claude ──', disabled: true },
                            { value: 'claude-haiku-4-5-20251001', label: '⚡ Claude Haiku 4.5' },
                            { value: 'claude-sonnet-4-6', label: '✍️ Claude Sonnet 4.6' },
                            { value: 'claude-opus-4-6', label: '🌟 Claude Opus 4.6' },


                          ]}
                          placeholder="Select AI Model"
                        />

                      </div>

                      {/* Article Length */}
                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Article Length</label>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { value: 1200, label: '1200', sub: 'words' },
                            { value: 1600, label: '1600', sub: 'words' },
                            { value: 2000, label: '2000', sub: 'words' },
                            { value: 2500, label: '2500', sub: 'words' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, article_length: opt.value })}
                              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg border text-sm font-medium transition-all ${formData.article_length === opt.value
                                ? 'border-violet-500/60 bg-violet-500/10 text-violet-300'
                                : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20 hover:text-zinc-200'
                                }`}
                            >
                              <span className="font-semibold">{opt.label}</span>
                              <span className="text-xs opacity-60">{opt.sub}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Include Images toggle */}
                      <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/5 border border-white/10">
                        <div>
                          <p className="text-sm font-medium text-zinc-200">Include Images</p>
                          <p className="text-xs text-zinc-500 mt-0.5">Auto-generate images for this article</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, include_images: !formData.include_images })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.include_images ? 'bg-violet-600' : 'bg-zinc-700'
                            }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.include_images ? 'translate-x-6' : 'translate-x-1'
                              }`}
                          />
                        </button>
                      </div>

                      <div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <GlassSelect
                              label="WordPress Site"
                              value={formData.wordpress_site_id}
                              onChange={handleSiteChange}
                              options={[
                                { value: '', label: 'Select WordPress Site' },
                                ...sites.map(site => ({ value: site.id, label: site.site_name }))
                              ]}
                              placeholder="Select WordPress Site"
                            />
                          </div>

                          {formData.wordpress_site_id ? (
                            <div>
                              <GlassSelect
                                label={<span>Category <span style={{ color: '#f87171', marginLeft: 2 }}>*</span></span>}
                                value={formData.category_id}
                                onChange={(value) => setFormData({ ...formData, category_id: value })}
                                options={[
                                  { value: '', label: loadingCategories[formData.wordpress_site_id] ? 'Loading...' : 'Select Category (required)' },
                                  ...(categories[formData.wordpress_site_id] || []).map(cat => ({ value: cat.id, label: cat.name }))
                                ]}
                                placeholder="Select Category"
                              />
                              {!formData.category_id && (
                                <p style={{ fontSize: '11px', color: '#f87171', marginTop: '4px' }}>Category is required to generate an article.</p>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <GlassSelect
                              label="Publish Type"
                              value={formData.publish_type}
                              onChange={(value) => setFormData({ ...formData, publish_type: value })}
                              options={[
                                { value: 'draft', label: 'Draft' },
                                { value: 'publish', label: 'Publish Immediately' }
                              ]}
                              placeholder="Draft"
                            />
                          </div>

                          <div>
                            <GlassSelect
                              label="Schedule"
                              value={formData.schedule_type || 'now'}
                              onChange={(value) => setFormData({ ...formData, schedule_type: value })}
                              options={[
                                { value: 'now', label: 'Post Now' },
                                { value: 'scheduled', label: 'Schedule for Later' }
                              ]}
                              placeholder="Post Now"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-sm text-zinc-300">Review before publishing</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={requiresReview}
                              onChange={(e) => setRequiresReview(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                          </label>
                          <span className="text-xs text-zinc-500 font-medium">{requiresReview ? 'ON' : 'OFF'}</span>
                        </div>

                        {formData.schedule_type === 'scheduled' && (
                          <div className="mt-3">
                            {useMonthScheduler ? (
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <GlassSelect
                                    label="Month"
                                    value={scheduleMonth}
                                    onChange={(value) => setScheduleMonth(value)}
                                    options={[
                                      { value: '', label: 'Select Month' },
                                      ...['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => ({ value: month, label: month }))
                                    ]}
                                    placeholder="Select Month"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Day</label>
                                  <input
                                    type="number"
                                    className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors"
                                    value={scheduleDay}
                                    onChange={(e) => setScheduleDay(e.target.value)}
                                    min="1"
                                    max="31"
                                    placeholder="15"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Time</label>
                                  <input
                                    type="time"
                                    className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors cursor-pointer [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                    onClick={(e) => e.target.showPicker?.()}
                                    required
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                                <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Schedule Date & Time</label>
                                <input
                                  type="datetime-local"
                                  className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors"
                                  value={formData.scheduled_at || ''}
                                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                                  min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
                                  required
                                />
                                <p className="text-xs text-zinc-500 mt-1.5">Minimum 1 hour from now — article needs time to generate first</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                          {error}
                        </div>
                      )}
                    </form>
                  ) : activeTab === 'cluster' ? (
                    <form onSubmit={handleGenerateCluster} className="space-y-5">

                      {/* Success message */}
                      {clusterSuccess && (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <p className="text-sm text-emerald-400 font-medium">✅ {clusterSuccess.count} articles added to queue!</p>
                          <p className="text-xs text-zinc-400 mt-1">Pillar: "{clusterSuccess.pillar}"</p>
                          <div className="flex gap-2 mt-3">
                            <button type="button" onClick={() => { setClusterSuccess(null); }} className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-zinc-300 transition-colors">Generate Another</button>
                            <button type="button" onClick={() => onClose && onClose('topics')} className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-md text-white transition-colors">View Topic Queue</button>
                          </div>
                        </div>
                      )}

                      {/* Seed Keyword */}
                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Seed Keyword</label>
                        <input
                          type="text"
                          className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors"
                          placeholder="e.g., Coffee, Camping, Digital Marketing"
                          value={clusterFormData.seed_keyword}
                          onChange={(e) => setClusterFormData({ ...clusterFormData, seed_keyword: e.target.value })}
                          required
                        />
                        <p className="text-xs text-zinc-500 mt-1.5">Enter a broad topic — AI will create 1 pillar + 10 cluster articles</p>
                      </div>

                      {/* Site + Category */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <GlassSelect
                            label="WordPress Site"
                            value={clusterFormData.wordpress_site_id}
                            onChange={handleClusterSiteChange}
                            options={[
                              { value: '', label: 'Select WordPress Site' },
                              ...sites.map(site => ({ value: site.id, label: site.site_name }))
                            ]}
                            placeholder="Select WordPress Site"
                          />
                        </div>
                        <div>
                          <GlassSelect
                            label="Category *"
                            value={clusterFormData.category_id}
                            onChange={(value) => setClusterFormData({ ...clusterFormData, category_id: value })}
                            options={[
                              { value: '', label: clusterFormData.wordpress_site_id ? 'Select Category' : 'Select site first' },
                              ...(categories[clusterFormData.wordpress_site_id] || []).map(cat => ({ value: cat.id, label: cat.name }))
                            ]}
                            placeholder="Select Category"
                          />
                        </div>
                      </div>

                      {/* Target Location */}
                      <div>
                        <GlassSelect
                          label="Target Location"
                          value={clusterFormData.target_location}
                          onChange={(value) => {
                            const locationData = require('../data/locations').DATAFORSEO_LOCATIONS[value];
                            setClusterFormData({ ...clusterFormData, target_location: value, location_code: locationData?.code || 2840 });
                          }}
                          options={[
                            { value: 'US', label: '🇺🇸 United States' },
                            { value: 'GB', label: '🇬🇧 United Kingdom' },
                            { value: 'CA', label: '🇨🇦 Canada' },
                            { value: 'AU', label: '🇦🇺 Australia' },
                            { value: 'IN', label: '🇮🇳 India' },
                            { value: 'DE', label: '🇩🇪 Germany' },
                            { value: 'FR', label: '🇫🇷 France' },
                            { value: 'ES', label: '🇪🇸 Spain' },
                            { value: 'IT', label: '🇮🇹 Italy' },
                            { value: 'BR', label: '🇧🇷 Brazil' },
                            { value: 'MX', label: '🇲🇽 Mexico' },
                            { value: 'NL', label: '🇳🇱 Netherlands' },
                            { value: 'SG', label: '🇸🇬 Singapore' },
                            { value: 'AE', label: '🇦🇪 UAE' },
                            { value: 'ZA', label: '🇿🇦 South Africa' },
                            { value: 'JP', label: '🇯🇵 Japan' },
                            { value: 'KR', label: '🇰🇷 South Korea' },
                            { value: 'PH', label: '🇵🇭 Philippines' },
                            { value: 'MY', label: '🇲🇾 Malaysia' },
                            { value: 'TH', label: '🇹🇭 Thailand' },
                          ]}
                          placeholder="Select Target Location"
                        />
                        <p className="text-xs text-zinc-500 mt-1.5">🌍 All 11 articles will be optimized for this location</p>
                      </div>

                      {/* AI Model */}
                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">AI Model</label>
                        <GlassSelect
                          value={clusterFormData.ai_model}
                          onChange={(value) => setClusterFormData({ ...clusterFormData, ai_model: value })}
                          options={[
                            { value: '', label: '── Gemini ──', disabled: true },
                            { value: 'gemini-2.5-flash-lite', label: '⚡ Gemini 2.5 Flash Lite' },
                            { value: 'gemini-2.5-flash', label: '🔥 Gemini 2.5 Flash' },
                            { value: 'gemini-2.5-pro', label: '🌟 Gemini 2.5 Pro' },
                            { value: '', label: '── ChatGPT ──', disabled: true },
                            { value: 'gpt-4o-mini', label: '💰 GPT-4o Mini' },
                            { value: 'gpt-5-nano-2025-08-07', label: '💰 GPT-5 Nano' },
                            { value: 'gpt-5-mini-2025-08-07', label: '💰 GPT-5 Mini' },
                            { value: 'gpt-4.1-mini', label: '⚡ GPT-4.1 Mini' },
                            { value: 'gpt-5.2', label: '🚀 GPT-5.2' },
                            { value: '', label: '── Claude ──', disabled: true },
                            { value: 'claude-haiku-4-5-20251001', label: '⚡ Claude Haiku 4.5' },
                            { value: 'claude-sonnet-4-6', label: '✍️ Claude Sonnet 4.6' },
                            { value: 'claude-opus-4-6', label: '🌟 Claude Opus 4.6' },
                          ]}
                          placeholder="Select AI Model"
                        />
                      </div>

                      {/* Article Length */}
                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Article Length</label>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { value: 1200, label: '1200', sub: 'words' },
                            { value: 1600, label: '1600', sub: 'words' },
                            { value: 2000, label: '2000', sub: 'words' },
                            { value: 2500, label: '2500', sub: 'words' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setClusterFormData({ ...clusterFormData, article_length: opt.value })}
                              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg border text-sm font-medium transition-all ${clusterFormData.article_length === opt.value
                                ? 'border-violet-500/60 bg-violet-500/10 text-violet-300'
                                : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20 hover:text-zinc-200'
                                }`}
                            >
                              <span className="font-semibold">{opt.label}</span>
                              <span className="text-xs opacity-60">{opt.sub}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Include Images */}
                      <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/5 border border-white/10">
                        <div>
                          <p className="text-sm font-medium text-zinc-200">Include Images</p>
                          <p className="text-xs text-zinc-500 mt-0.5">Auto-generate images for each article</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setClusterFormData({ ...clusterFormData, include_images: !clusterFormData.include_images })}
                          style={{ position:'relative', display:'inline-flex', alignItems:'center', width:'44px', height:'24px', borderRadius:'9999px', border:'none', cursor:'pointer', padding:0, flexShrink:0, transition:'background 0.2s', background: clusterFormData.include_images ? '#7c3aed' : '#3f3f46', outline:'none' }}
                        >
                          <span style={{ display:'inline-block', width:'18px', height:'18px', borderRadius:'9999px', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.4)', transition:'transform 0.2s', transform: clusterFormData.include_images ? 'translateX(22px)' : 'translateX(3px)' }} />
                        </button>
                      </div>

                      {/* Publish Type + Review */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <GlassSelect
                            label="Publish Type"
                            value={clusterFormData.publish_type}
                            onChange={(value) => setClusterFormData({ ...clusterFormData, publish_type: value })}
                            options={[
                              { value: 'draft', label: 'Draft' },
                              { value: 'publish', label: 'Publish Immediately' }
                            ]}
                            placeholder="Draft"
                          />
                        </div>
                        <div className="flex flex-col justify-end">
                          <div className="flex items-center gap-3 h-11">
                            <span className="text-sm text-zinc-300">Review before publishing</span>
                            <button
                              type="button"
                              onClick={() => setClusterFormData({ ...clusterFormData, requires_review: !clusterFormData.requires_review })}
                              style={{ position:'relative', display:'inline-flex', alignItems:'center', width:'44px', height:'24px', borderRadius:'9999px', border:'none', cursor:'pointer', padding:0, flexShrink:0, transition:'background 0.2s', background: clusterFormData.requires_review ? '#7c3aed' : '#3f3f46', outline:'none' }}
                            >
                              <span style={{ display:'inline-block', width:'18px', height:'18px', borderRadius:'9999px', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.4)', transition:'transform 0.2s', transform: clusterFormData.requires_review ? 'translateX(22px)' : 'translateX(3px)' }} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Scheduling */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <GlassSelect
                            label="Schedule"
                            value={clusterFormData.schedule_type}
                            onChange={(value) => setClusterFormData({ ...clusterFormData, schedule_type: value })}
                            options={[
                              { value: 'now', label: 'Post Now' },
                              { value: 'scheduled', label: 'Schedule for Later' },
                            ]}
                            placeholder="Post Now"
                          />
                        </div>
                        {clusterFormData.schedule_type === 'scheduled' && (
                          <div>
                            <GlassSelect
                              label="Interval"
                              value={String(clusterFormData.interval_days)}
                              onChange={(value) => setClusterFormData({ ...clusterFormData, interval_days: parseInt(value) })}
                              options={[
                                { value: '0', label: 'Same day (queue order)' },
                                { value: '1', label: 'Every 1 day' },
                                { value: '2', label: 'Every 2 days' },
                                { value: '3', label: 'Every 3 days' },
                                { value: '7', label: 'Every week' },
                              ]}
                              placeholder="Same day"
                            />
                          </div>
                        )}
                      </div>

                      {clusterFormData.schedule_type === 'scheduled' && (
                        <div>
                          <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">
                            Start Date & Time
                            <span className="text-zinc-500 ml-1 font-normal">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
                          </label>
                          <input
                            type="datetime-local"
                            className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors"
                            value={clusterFormData.start_date}
                            onChange={(e) => setClusterFormData({ ...clusterFormData, start_date: e.target.value })}
                            min={new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16)}
                            required
                          />
                          {clusterFormData.start_date && (
                            <p className="text-xs text-zinc-500 mt-1.5">
                              {parseInt(clusterFormData.interval_days) === 0
                                ? `All 11 articles will publish on ${new Date(clusterFormData.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} in queue order`
                                : `Pillar publishes ${new Date(clusterFormData.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, then 1 article every ${clusterFormData.interval_days} day${parseInt(clusterFormData.interval_days) > 1 ? 's' : ''} — last article on ${new Date(new Date(clusterFormData.start_date).getTime() + parseInt(clusterFormData.interval_days) * 10 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              }
                            </p>
                          )}
                        </div>
                      )}

                      {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                          {error}
                        </div>
                      )}
                    </form>
                  ) : activeTab === 'affiliate' ? (
                    /* Product Review / Affiliate Tab */
                    <form onSubmit={handleGenerateAffiliateArticle} className="space-y-5">

                      {/* Success message */}
                      {affiliateSuccess && (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <p className="text-sm text-emerald-400 font-medium">✅ Product review queued successfully!</p>
                          <p className="text-xs text-zinc-400 mt-1">Article will generate in the background and appear in Topic Queue.</p>
                          <div className="flex gap-2 mt-3">
                            <button type="button" onClick={() => setAffiliateSuccess(false)} className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-zinc-300 transition-colors">Generate Another</button>
                            <button type="button" onClick={() => onClose && onClose('topics')} className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-md text-white transition-colors">View Topic Queue</button>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Product / Topic</label>
                        <input
                          type="text"
                          className="h-11 w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors"
                          placeholder='e.g. "Best laptops under $1000" or "Dyson V15 Review"'
                          value={affiliateFormData.topic}
                          onChange={e => setAffiliateFormData({ ...affiliateFormData, topic: e.target.value })}
                          required
                        />
                        <p className="text-xs text-zinc-500 mt-1.5">Generates a product review article with Amazon affiliate links</p>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                          <div>
                            <GlassSelect
                              label="WordPress Site"
                              value={affiliateFormData.wordpress_site_id}
                              onChange={handleAffiliateSiteChange}
                              options={[
                                { value: '', label: 'Select WordPress Site' },
                                ...sites.map(site => ({ value: site.id, label: site.site_name }))
                              ]}
                              placeholder="Select WordPress Site"
                            />
                          </div>

                          {affiliateFormData.wordpress_site_id && (
                            <div>
                              <GlassSelect
                                label="Category"
                                value={affiliateFormData.category_id}
                                onChange={value => setAffiliateFormData({ ...affiliateFormData, category_id: value })}
                                options={[
                                  { value: '', label: 'Select Category' },
                                  ...(categories[affiliateFormData.wordpress_site_id] || []).map(cat => ({ value: cat.id, label: cat.name }))
                                ]}
                                placeholder="Select Category"
                              />
                            </div>
                          )}
                        </div>

                        {/* Amazon Tag inline editor */}
                        {affiliateFormData.wordpress_site_id && (
                          <div className="mt-4">
                            <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">Amazon Associate Tag</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                className="h-10 flex-1 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50 transition-colors"
                                placeholder="mytag-20"
                                value={amazonTag}
                                onChange={e => { setAmazonTag(e.target.value); setAmazonTagSaved(false); }}
                              />
                              <button
                                type="button"
                                onClick={saveAmazonTag}
                                disabled={savingAmazonTag}
                                className="h-10 px-3 rounded-lg border border-white/10 text-xs text-zinc-400 hover:text-white hover:bg-white/5 transition-all disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap"
                              >
                                {amazonTagSaved ? (
                                  <><CheckCircledIcon className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">Saved</span></>
                                ) : savingAmazonTag ? (
                                  <><ReloadIcon className="w-3.5 h-3.5 animate-spin" /> Saving...</>
                                ) : (
                                  'Save Tag'
                                )}
                              </button>
                            </div>
                            <p className="text-xs text-zinc-500 mt-1.5">Saved once per site — auto-loaded next time you select this site</p>
                          </div>
                        )}

                        {/* Number of Products */}
                        <div className="mt-4">
                          {isSingleProductTopic ? (
                            <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <span className="text-amber-400 text-lg">🎯</span>
                              <div>
                                <p className="text-sm text-amber-300 font-medium">Single Product Review detected</p>
                                <p className="text-xs text-zinc-400 mt-0.5">Your topic refers to a specific product — generating a deep-dive review instead of a roundup.</p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <label className="text-sm leading-none font-medium text-muted-foreground mb-3 block">
                                Number of Products: <span className="text-white font-semibold">{affiliateFormData.num_products}</span>
                                <span className="text-zinc-600 ml-2">{affiliateFormData.num_products === 1 ? '(Single product review)' : '(Roundup / comparison)'}</span>
                              </label>
                              <input
                                type="range"
                                min="1" max="10"
                                value={affiliateFormData.num_products}
                                onChange={e => setAffiliateFormData({ ...affiliateFormData, num_products: parseInt(e.target.value) })}
                                className="w-full accent-amber-400"
                              />
                              <div className="flex justify-between text-xs text-zinc-600 mt-1">
                                <span>1 (Single)</span><span>10 (Roundup)</span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* AI Model */}
                        <div className="mt-4">
                          <label className="text-sm leading-none font-medium text-muted-foreground mb-1.5 block">AI Model</label>
                          <GlassSelect
                            value={affiliateFormData.ai_model}
                            onChange={value => setAffiliateFormData({ ...affiliateFormData, ai_model: value })}
                            options={[
                              { value: '', label: '── Gemini ──', disabled: true },
                              { value: 'gemini-2.5-flash-lite', label: '⚡ Gemini 2.5 Flash Lite' },
                              { value: 'gemini-2.5-flash', label: '🔥 Gemini 2.5 Flash' },
                              { value: 'gemini-2.5-pro', label: '🌟 Gemini 2.5 Pro' },
                              { value: '', label: '── ChatGPT ──', disabled: true },
                              { value: 'gpt-4o-mini', label: '💰 GPT-4o Mini' },
                              { value: 'gpt-4.1-mini', label: '⚡ GPT-4.1 Mini' },
                              { value: 'gpt-5-mini-2025-08-07', label: '💰 GPT-5 Mini' },
                              { value: '', label: '── Claude ──', disabled: true },
                              { value: 'claude-haiku-4-5-20251001', label: '⚡ Claude Haiku 4.5' },
                              { value: 'claude-sonnet-4-6', label: '✍️ Claude Sonnet 4.6' },
                            ]}
                            placeholder="Select AI Model"
                          />
                        </div>

                        {/* Target Location */}
                        <div className="mt-4">
                          <GlassSelect
                            label="Target Location"
                            value={affiliateFormData.target_location}
                            onChange={value => {
                              const locationData = require('../data/locations').DATAFORSEO_LOCATIONS[value];
                              setAffiliateFormData({ ...affiliateFormData, target_location: value, location_code: locationData?.code || 2840 });
                            }}
                            options={[
                              { value: 'US', label: '🇺🇸 United States' },
                              { value: 'GB', label: '🇬🇧 United Kingdom' },
                              { value: 'CA', label: '🇨🇦 Canada' },
                              { value: 'AU', label: '🇦🇺 Australia' },
                              { value: 'IN', label: '🇮🇳 India' },
                              { value: 'DE', label: '🇩🇪 Germany' },
                              { value: 'FR', label: '🇫🇷 France' },
                              { value: 'SG', label: '🇸🇬 Singapore' },
                              { value: 'AE', label: '🇦🇪 UAE' },
                            ]}
                            placeholder="Select Target Location"
                          />
                        </div>

                        <div className="mt-4">
                          <GlassSelect
                            label="Publish Type"
                            value={affiliateFormData.publish_type}
                            onChange={value => setAffiliateFormData({ ...affiliateFormData, publish_type: value })}
                            options={[
                              { value: 'draft', label: 'Draft' },
                              { value: 'publish', label: 'Publish Immediately' }
                            ]}
                            placeholder="Draft"
                          />
                        </div>
                      </div>

                      {affiliateError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                          {affiliateError}
                        </div>
                      )}
                    </form>
                  ) : (
                    /* Batch Import Tab */
                    <div className="space-y-5">
                      {batchResult?.queued && (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <p className="text-sm text-emerald-400 font-medium">✅ {batchResult.queued} topics queued for generation!</p>
                          <button type="button" onClick={() => { setBatchResult(null); if (onClose) onClose('topics'); }} className="text-xs text-zinc-400 hover:text-white mt-2 underline">View Topic Queue</button>
                        </div>
                      )}
                      {batchResult?.error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">{batchResult.error}</div>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-sm leading-none font-medium text-muted-foreground">Topics</label>
                          <span className="text-xs text-zinc-500">
                            {batchText.split('\n').filter(l => l.trim()).length} / 100 topics
                          </span>
                        </div>
                        <textarea
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors resize-none"
                          rows={8}
                          placeholder={"One topic per line:\nHow to start a blog in 2025\nBest coffee grinders for beginners\nHome office setup ideas on a budget"}
                          value={batchText}
                          onChange={e => setBatchText(e.target.value)}
                        />
                        <p className="text-xs text-zinc-500 mt-1.5">Paste up to 100 topics — one per line. All will use the settings below.</p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <GlassSelect
                          label="WordPress Site"
                          value={formData.wordpress_site_id}
                          onChange={handleSiteChange}
                          options={[
                            { value: '', label: 'Select WordPress Site' },
                            ...sites.map(site => ({ value: site.id, label: site.site_name }))
                          ]}
                          placeholder="Select WordPress Site"
                        />
                        {formData.wordpress_site_id ? (
                          <GlassSelect
                            label="Category *"
                            value={formData.category_id}
                            onChange={(value) => setFormData({ ...formData, category_id: value })}
                            options={[
                              { value: '', label: loadingCategories[formData.wordpress_site_id] ? 'Loading...' : 'Select Category' },
                              ...(categories[formData.wordpress_site_id] || []).map(cat => ({ value: cat.id, label: cat.name }))
                            ]}
                            placeholder="Select Category"
                          />
                        ) : null}
                      </div>

                      <GlassSelect
                        label="AI Model"
                        value={formData.ai_model}
                        onChange={(value) => setFormData({ ...formData, ai_model: value })}
                        options={[
                          { value: 'gemini-2.5-flash', label: '🔥 Gemini 2.5 Flash' },
                          { value: 'gemini-2.5-pro', label: '🌟 Gemini 2.5 Pro' },
                          { value: 'gpt-4o-mini', label: '💰 GPT-4o Mini' },
                          { value: 'claude-sonnet-4-6', label: '✍️ Claude Sonnet 4.6' },
                        ]}
                        placeholder="Select AI Model"
                      />

                      <GlassSelect
                        label="Publish Type"
                        value={formData.publish_type}
                        onChange={(value) => setFormData({ ...formData, publish_type: value })}
                        options={[
                          { value: 'draft', label: 'Draft' },
                          { value: 'publish', label: 'Publish Immediately' },
                        ]}
                        placeholder="Draft"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* FOOTER (BOTTOM) */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3 flex-none">
                <button
                  type="button"
                  className="px-4 py-2.5 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
                  onClick={onClose}
                >
                  Cancel
                </button>
                {activeTab === 'single' ? (
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    onClick={handlePost}
                    disabled={loading || !formData.wordpress_site_id || !formData.category_id}
                  >
                    {loading ? 'Generating...' : 'Generate Article'}
                  </button>
                ) : activeTab === 'cluster' ? (
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    onClick={handleGenerateCluster}
                    disabled={clusterLoading}
                  >
                    {clusterLoading ? 'AI is brainstorming...' : 'Generate Content Strategy'}
                  </button>
                ) : activeTab === 'affiliate' ? (
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    onClick={handleGenerateAffiliateArticle}
                    disabled={affiliateLoading || !affiliateFormData.topic || !affiliateFormData.wordpress_site_id}
                  >
                    {affiliateLoading ? 'Generating...' : '🛒 Generate Review Article'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    onClick={handleBatchSubmit}
                    disabled={batchLoading || !formData.wordpress_site_id || !formData.category_id || !batchText.trim()}
                  >
                    {batchLoading ? 'Queuing...' : `📋 Queue ${batchText.split('\n').filter(l => l.trim()).length || 0} Topics`}
                  </button>
                )}
              </div>
            </>
          ) : (
            /* Focus mode — full-screen generation overlay */
            <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center overflow-hidden">
              {/* Close / background-dismiss */}
              <button
                type="button"
                className="absolute top-5 right-6 text-zinc-500 hover:text-zinc-200 text-2xl transition-colors"
                onClick={() => { setShowProgress(false); setLoading(false); if (onClose) onClose(); }}
                title="Dismiss (generation continues in background)"
              >×</button>

              <div className="w-full max-w-lg px-6">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-xl font-semibold text-white mb-1">Generating your article</h3>
                  <p className="text-sm text-zinc-500">AI pipeline is running — this takes 1–3 minutes</p>
                </div>

                {/* Overall progress bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
                    <span>Progress</span>
                    <span>{topicProgress}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-600 to-blue-500 transition-all duration-700"
                      style={{ width: `${topicProgress}%` }}
                    />
                  </div>
                </div>

                {/* 16-node pipeline checklist */}
                {(() => {
                  const nodes = [
                    'KeywordExtractor', 'TitleGenerator', 'OutlineGenerator', 'SectionContentGenerator',
                    'IntroductionGenerator', 'ConclusionGenerator', 'FAQGenerator', 'KeyTakeawaysGenerator',
                    'FactBoxGenerator', 'InlineImageGenerator', 'TitleValidator', 'IntroductionValidator',
                    'OutlineValidator', 'SectionContentValidator', 'RoboticWordDetector', 'ContentAssembly'
                  ];
                  const nodesPerPercent = 100 / nodes.length;
                  const activeNodeIndex = Math.min(
                    Math.floor(topicProgress / nodesPerPercent),
                    nodes.length - 1
                  );
                  return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 max-h-72 overflow-y-auto pr-1">
                      {nodes.map((node, i) => {
                        const isDone   = topicProgress > 0 && i < activeNodeIndex;
                        const isActive = i === activeNodeIndex && topicProgress > 0 && topicProgress < 100;
                        return (
                          <div key={node} className={`flex items-center gap-2 text-xs py-0.5 ${
                            isDone   ? 'text-emerald-400' :
                            isActive ? 'text-violet-300 font-semibold' :
                                       'text-zinc-600'
                          }`}>
                            {isDone   && <CheckIcon className="h-3 w-3 flex-shrink-0 text-emerald-500" />}
                            {isActive && <ReloadIcon className="h-3 w-3 flex-shrink-0 text-violet-400 animate-spin" />}
                            {!isDone && !isActive && <span className="h-3 w-3 flex-shrink-0 rounded-full border border-zinc-700 inline-block" />}
                            <span>{node}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {error && (
                  <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 mb-3">{error}</p>
                    <button
                      onClick={handleRetry}
                      className="px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium rounded-lg transition-all"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuickPost;
